import type { ReactElement } from 'react'

import AnalyticsPreview from './AnalyticsPreview'

import MwMLoader from '~/components/MwMLoader'
import { dataNotAvailableText } from '~/const'
import { useAppSummary } from '~/providers/AppSummary'
import { useTopLevelAppAnalyticsQuery } from '~/queries/analytics'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'

import './styles.scss'

interface Props {
  queryFn?: (feature: string) => Promise<any>
}

const LegacyBlock = ({ queryFn }: Props): ReactElement => {
  const { appKey, deviceType } = useAppSummary()

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { data: appStartCount, isLoading: appStartCountLoading } = useTopLevelAppAnalyticsQuery(
    'ui_app_start_count',
    appSummary?.app_id,
    undefined,
    queryFn
  )

  const { data: installCount, isLoading: installCountLoading } = useTopLevelAppAnalyticsQuery(
    'ui_install_count',
    appSummary?.app_id,
    undefined,
    queryFn
  )

  const { data: dau, isLoading: dauLoading } = useTopLevelAppAnalyticsQuery(
    'ui_dau',
    appSummary?.app_id,
    undefined,
    queryFn
  )

  const { data: wau, isLoading: wauLoading } = useTopLevelAppAnalyticsQuery(
    'ui_wau',
    appSummary?.app_id,
    undefined,
    queryFn
  )

  const { data: mau, isLoading: mauLoading } = useTopLevelAppAnalyticsQuery(
    'ui_mau',
    appSummary?.app_id,
    undefined,
    queryFn
  )

  const isFullLoaded = !appStartCountLoading && !installCountLoading && !dauLoading && !wauLoading && !mauLoading

  const isDataAvailable = (!!appStartCount || !!installCount || !!dau || !!wau || !!mau) && isFullLoaded

  if (!isFullLoaded) {
    return <MwMLoader category="item" />
  }

  return (
    <div className="legacy-block-component">
      <div className="analytics-preview-wrapper">
        <AnalyticsPreview
          areSiblingsLoaded={isFullLoaded}
          doubleComparaison
          metrics={appStartCount}
          period="d"
          title="App Starts"
        />
      </div>
      <div className="analytics-preview-wrapper">
        <AnalyticsPreview
          areSiblingsLoaded={isFullLoaded}
          doubleComparaison
          metrics={installCount}
          period="d"
          title="Installs"
        />
      </div>
      <div className="analytics-preview-wrapper">
        <AnalyticsPreview
          areSiblingsLoaded={isFullLoaded}
          doubleComparaison
          metrics={dau}
          period="d"
          title="Daily Active Users (DAU)"
        />
      </div>
      <div className="analytics-preview-wrapper">
        <AnalyticsPreview
          areSiblingsLoaded={isFullLoaded}
          metrics={wau}
          period="w"
          title="Weekly Active Users (WAU)"
          wau
        />
      </div>
      <div className="analytics-preview-wrapper">
        <AnalyticsPreview
          areSiblingsLoaded={isFullLoaded}
          metrics={mau}
          period="m"
          title="Monthly Active Users (MAU)"
        />
      </div>
      {!isDataAvailable && <span>{dataNotAvailableText}</span>}
    </div>
  )
}

export default LegacyBlock
